// @givmo/board — a member's getting-started list, on their page in the console (CBG-YR4-START, g1).
//
// One row per item: what it is, which version, its state in plain words, and the one act an officer
// has on it — send the year's conflict-of-interest questionnaire (CBG-YR2's own Send and its email),
// choose a board buddy, or mark one of the board's own steps done. The member does the rest
// themselves; a signed paper form, which usually covers several documents, is one act for the card.
// No progress bar and no count: the rows say it (the COI dashboard's rule). Nothing here is required,
// and nothing on the list touches the member's vote, quorum count or reads (contract §12 I2).
//
// Each act holds its own string: the list's writes are `board_member:grant_access` (Member and Owner),
// the Send is the year's own act, `board_conflict_disclosure:manage`. The close is one way, behind a
// dialog that says so (C16), and is offered from the list's editor: the card keeps the frame's acts.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError, type OrgBoardMemberOut } from '@givmo/api-client';
import { Button, Card, CardBody, testIdProp, toast } from '@givmo/design-system';
import { HowThisWorks, StatusWord } from '../../components/fine-print';
import { hasBoardPermission, useBoardHost } from '../../board-host';
import { Link } from '../../board-routing';
import { BOARD_PAGES } from '../../board-routes';
import { useBoardOperations } from '../../board-runtime';
import { todayAsDateInput } from '../../lib/display';
import { shortDay } from '../conflicts/coi-decode';
import { SendDialog } from '../conflicts/coi-officer-dialogs';
import { useCoiCycle, useRefreshCoi } from '../conflicts/coi-queries';
import { extractBoardMembers, isCommitteeOnlySeat } from '../roster/roster-decode';
import { capacityLine } from '../roster/officer-roles';
import {
  BuddyDialog,
  CloseListDialog,
  ListEditorDialog,
  PaperFormDialog,
  useDocumentChoices,
  type PaperFormValues,
} from './onboarding-dialogs';
import {
  extractMemberList,
  itemDetail,
  itemStatus,
  itemTitle,
  titlesOf,
  paperCandidates,
  toSpec,
  type ItemSpec,
} from './onboarding-decode';
import { onboardingWords as w } from './onboarding-words';

const ACT =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';
const LINE = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';

type Open = 'paper' | 'buddy' | 'edit' | 'close' | { send: string } | null;

function failure(caught: unknown, fallback: string): string {
  return isGivmoError(caught) ? caught.message : fallback;
}

export function GettingStartedCard({ seat }: { seat: OrgBoardMemberOut }) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const { orgRef, isReady, permissions, orgName } = useBoardHost();
  const canAct = hasBoardPermission(permissions, 'board_member:grant_access');
  const canSend = hasBoardPermission(permissions, 'board_conflict_disclosure:manage');
  const [open, setOpen] = useState<Open>(null);
  const [error, setError] = useState<string | null>(null);
  const firstName = (seat.display_name ?? '').trim().split(/\s+/)[0] ?? '';
  const path = { org_ref: orgRef ?? '', member_id: seat.id };

  const listOptions = operations.officerGetBoardMemberGettingStartedOptions({ path });
  const listQuery = useQuery({
    ...listOptions,
    enabled: isReady && orgRef !== null,
    select: extractMemberList,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: listOptions.queryKey });

  // Who can be a buddy: someone serving on this board, not the member themselves.
  const seatsQuery = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: orgRef ?? '' } }),
    enabled: isReady && orgRef !== null && open === 'buddy',
    select: extractBoardMembers,
  });
  // The documents the editor offers: each governing document with a version in force.
  const { documents, failed: documentsFailed } = useDocumentChoices(open === 'edit');
  const sendCycle = useCoiCycle(
    orgRef,
    typeof open === 'object' && open !== null ? open.send : null,
  );
  const refreshCoi = useRefreshCoi(orgRef);

  const done = (message?: string) => async () => {
    setOpen(null);
    setError(null);
    await refresh();
    if (message) toast.success(message);
  };

  const start = useMutation({
    mutationFn: async () => {
      await operations.officerStartBoardMemberGettingStarted({ path, throwOnError: true });
    },
    onSuccess: done(),
    onError: (caught) =>
      toast.error(failure(caught, 'We couldn’t start the list. Please try again.')),
  });
  const recordPaper = useMutation({
    mutationFn: async (values: PaperFormValues) => {
      await operations.officerRecordBoardPaperAcknowledgments({
        path,
        body: values,
        throwOnError: true,
      });
    },
    onSuccess: done(w.paperDone),
    onError: (caught) => setError(failure(caught, 'We couldn’t record it. Please try again.')),
  });
  const chooseBuddy = useMutation({
    mutationFn: async (buddyId: string) => {
      await operations.officerChooseBoardMemberBuddy({
        path,
        body: { board_member_id: buddyId },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await done()();
      // The choice is also a line in the member's History on this page (a `buddy_chosen` entry):
      // refresh it too, so it shows without a reload (review-172 F8).
      await queryClient.invalidateQueries({
        queryKey: ['board-member-history', orgRef, seat.id],
      });
    },
    onError: (caught) => setError(failure(caught, 'We couldn’t choose them. Please try again.')),
  });
  const saveItems = useMutation({
    mutationFn: async ({ revision, items }: { revision: number; items: ItemSpec[] }) => {
      await operations.officerSaveBoardMemberGettingStarted({
        path,
        body: { revision, items },
        throwOnError: true,
      });
    },
    onSuccess: done(),
    onError: (caught) => setError(failure(caught, 'We couldn’t save the list. Please try again.')),
  });
  const markStep = useMutation({
    mutationFn: async ({ itemId, doneOn }: { itemId: string; doneOn: string | null }) => {
      await operations.officerMarkBoardMemberGettingStartedStep({
        path: { ...path, item_id: itemId },
        body: { done_on: doneOn },
        throwOnError: true,
      });
    },
    onSuccess: done(),
    onError: (caught) => toast.error(failure(caught, 'We couldn’t mark it. Please try again.')),
  });
  const close = useMutation({
    mutationFn: async () => {
      await operations.officerCloseBoardMemberGettingStarted({ path, throwOnError: true });
    },
    onSuccess: done(),
    onError: (caught) =>
      toast.error(failure(caught, 'We couldn’t close the list. Please try again.')),
  });

  const list = listQuery.data ?? null;
  if (list === null) return null;

  // No list yet: an officer starts one from the board's adopted list, or chooses the board's first.
  if (!list.started) {
    if (!canAct) return null;
    return (
      <Card testId="member-getting-started">
        <CardBody className="flex flex-col items-start gap-10">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            {w.cardTitle}
          </h2>
          {list.board_list_adopted ? (
            <>
              <p className={LINE}>{w.noListYet}</p>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                loading={start.isPending}
                onClick={() => start.mutate()}
                testId="gs-start"
              >
                {w.startList}
              </Button>
            </>
          ) : (
            <p className={LINE}>
              {w.boardListNotChosen}{' '}
              <Link
                to={BOARD_PAGES.members}
                search={{ tab: 'getting-started' }}
                className={ACT}
                {...testIdProp('gs-choose-board-list')}
              >
                {w.chooseBoardList}
              </Link>
            </p>
          )}
        </CardBody>
      </Card>
    );
  }

  const items = list.items ?? [];
  const closed = Boolean(list.closed_at);
  const writable = canAct && !closed;
  const buddyName = list.buddy?.display_name ?? null;
  const candidates = paperCandidates(items);
  const sendFor = typeof open === 'object' && open !== null ? sendCycle.data : null;
  const sendPerson = sendFor?.people.find((row) => row.board_member_id === seat.id) ?? null;

  return (
    <Card testId="member-getting-started">
      <CardBody className="flex flex-col gap-16">
        <div className="flex flex-col gap-4">
          <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
            {w.cardTitle}
          </h2>
          <p className={LINE}>
            {w.cardLead} <HowThisWorks testId="member-getting-started-how" lines={w.howLines} />
          </p>
          {closed ? (
            <p className={LINE} data-testid="gs-closed">
              {w.closedLine(shortDay(list.closed_at))}
            </p>
          ) : null}
        </div>
        <ul className="flex flex-col">
          {items.map((item) => {
            const status = itemStatus(item, buddyName);
            const detail = itemDetail(item);
            const disclosure = item.disclosure ?? null;
            return (
              <li
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-x-16 gap-y-6 border-t border-border-subtle py-12 first:border-t-0 first:pt-0 last:pb-0"
                data-testid={`gs-row-${item.id}`}
              >
                <div className="flex min-w-0 flex-col gap-2">
                  <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                    {itemTitle(item)}
                  </span>
                  {detail !== null ? (
                    <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                      {detail}
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center justify-end gap-x-16 gap-y-6 max-sm:w-full max-sm:justify-between">
                  <StatusWord tone={status.tone} testId={`gs-status-${item.id}`}>
                    {status.word}
                  </StatusWord>
                  {item.item_kind === 'disclosure' &&
                  !closed &&
                  canSend &&
                  disclosure?.stage === 'not_sent' &&
                  disclosure.can_send &&
                  disclosure.cycle_id ? (
                    <Button
                      appearance="saas"
                      tone="secondary"
                      size="sm"
                      onClick={() => setOpen({ send: disclosure.cycle_id ?? '' })}
                      testId={`gs-send-${item.id}`}
                    >
                      {w.sendQuestionnaire(disclosure.label ?? '')}
                    </Button>
                  ) : null}
                  {item.item_kind === 'buddy' && writable && buddyName === null ? (
                    <Button
                      appearance="saas"
                      tone="secondary"
                      size="sm"
                      onClick={() => setOpen('buddy')}
                      testId={`gs-buddy-${item.id}`}
                    >
                      {w.chooseBuddy}
                    </Button>
                  ) : null}
                  {item.item_kind === 'step' && writable ? (
                    <button
                      type="button"
                      className={ACT}
                      onClick={() =>
                        markStep.mutate({
                          itemId: item.id,
                          doneOn: item.step_done_on ? null : todayAsDateInput(),
                        })
                      }
                      data-testid={`gs-step-${item.id}`}
                    >
                      {item.step_done_on ? w.undoDone : w.markDone}
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
        {writable ? (
          <div className="flex flex-wrap items-center gap-x-16 gap-y-8">
            {candidates.length > 0 ? (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => setOpen('paper')}
                testId="gs-paper"
              >
                {w.recordPaperForm}
              </Button>
            ) : null}
            <button
              type="button"
              className={ACT}
              onClick={() => setOpen('edit')}
              data-testid="gs-edit"
            >
              {w.changeList}
            </button>
          </div>
        ) : null}
      </CardBody>

      {open === 'paper' ? (
        <PaperFormDialog
          memberName={seat.display_name ?? ''}
          candidates={candidates}
          pending={recordPaper.isPending}
          error={error}
          onCancel={() => {
            setOpen(null);
            setError(null);
          }}
          onConfirm={(values) => recordPaper.mutate(values)}
        />
      ) : null}
      {open === 'buddy' ? (
        <BuddyDialog
          firstName={firstName}
          people={(seatsQuery.data ?? [])
            // A board buddy is someone on the board (CBG-YR6-OUTSIDE §5.1, review-215's F6): a seat
            // for someone who isn't is never offered, as the backend refuses it.
            .filter(
              (other) =>
                other.id !== seat.id && other.status === 'active' && !isCommitteeOnlySeat(other),
            )
            .map((other) => ({
              id: other.id,
              label: `${other.display_name} · ${capacityLine(other)}`,
            }))}
          currentBuddyId={list.buddy?.board_member_id ?? null}
          pending={chooseBuddy.isPending}
          error={error}
          onCancel={() => {
            setOpen(null);
            setError(null);
          }}
          onConfirm={(buddyId) => chooseBuddy.mutate(buddyId)}
        />
      ) : null}
      {open === 'edit' && documents !== undefined ? (
        <ListEditorDialog
          title={w.editTitleMember(firstName)}
          lead={w.editLeadMember}
          initial={items.map((item) => toSpec(item, true))}
          titles={titlesOf(items)}
          documents={documents}
          documentsFailed={documentsFailed}
          pending={saveItems.isPending}
          error={error}
          onCancel={() => {
            setOpen(null);
            setError(null);
          }}
          onSave={(next) => saveItems.mutate({ revision: list.revision ?? 1, items: next })}
          onCloseList={() => {
            setError(null);
            setOpen('close');
          }}
        />
      ) : null}
      {open === 'close' ? (
        <CloseListDialog
          firstName={firstName}
          pending={close.isPending}
          onCancel={() => setOpen(null)}
          onConfirm={() => close.mutate()}
        />
      ) : null}
      {sendFor && sendPerson !== null && orgRef !== null ? (
        <SendDialog
          orgRef={orgRef}
          cycle={sendFor}
          person={sendPerson}
          organization={orgName?.trim() || 'Your organization'}
          onClose={() => {
            setOpen(null);
            void refresh();
            void refreshCoi();
          }}
        />
      ) : null}
    </Card>
  );
}
